import React, { useEffect, useRef, useState } from "react";

interface DebouncedRailInputProps {
  // Formatted store value. Reflected into the draft on resync (target switch, or an
  // external change while the field isn't focused).
  initial: string;
  // Identity of the edited target — issue id, or `${issueId}:${fieldId}` for custom
  // fields. When it changes the draft reloads and any pending write is dropped so it
  // can't land on the target we just left.
  resyncKey: string;
  // Parses/validates the raw string and writes it to the backend. Runs debounced and
  // on blur; should no-op when the value is unchanged.
  commit: (raw: string) => void;
  type?: "text" | "number";
  placeholder?: string;
  min?: number;
  max?: number;
  delayMs?: number;
}

// A rail text/number input whose source of truth is a remote store. It echoes
// keystrokes instantly from a local draft, debounces the backend write so typing
// doesn't fire a save per character, and flushes on blur. External store changes
// (quick-log buttons, remote edits) reflect when the field isn't focused, but never
// clobber what the user is actively typing.
const DebouncedRailInput: React.FC<DebouncedRailInputProps> = ({
  initial,
  resyncKey,
  commit,
  type = "text",
  placeholder,
  min,
  max,
  delayMs = 500
}) => {
  const [draft, setDraft] = useState<string>(initial);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timer = useRef<number | null>(null);
  const commitRef = useRef<(raw: string) => void>(commit);
  const initialRef = useRef<string>(initial);
  commitRef.current = commit;
  initialRef.current = initial;

  const clearTimer = (): void => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  // Target switched: reload the draft and drop any pending write for the old target.
  useEffect(() => {
    setDraft(initialRef.current);
    clearTimer();
  }, [resyncKey]);

  // External change to the same target (e.g. the +15m quick-log): reflect it, unless
  // the user is mid-edit in this very field.
  useEffect(() => {
    const isFocused: boolean = document.activeElement === inputRef.current;
    if (!isFocused) setDraft(initial);
  }, [initial]);

  // Drop a pending write if the field unmounts before the debounce fires.
  useEffect(() => clearTimer, []);

  const handleChange = (raw: string): void => {
    setDraft(raw); // echo the keystroke immediately
    clearTimer();
    timer.current = window.setTimeout(() => commitRef.current(raw), delayMs);
  };

  const handleBlur = (): void => {
    clearTimer();
    commitRef.current(draft);
  };

  return (
    <input
      ref={inputRef}
      className="kuika-board__rail-input"
      type={type}
      min={min}
      max={max}
      placeholder={placeholder}
      value={draft}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(event.target.value)}
      onBlur={handleBlur}
    />
  );
};

export default DebouncedRailInput;
